/**
 * AvatarCropperModal - 头像裁剪弹窗
 * 支持方形裁剪，输出压缩后的图片
 */

import React, { useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import Cropper from 'react-cropper';
import "cropperjs/dist/cropper.css";
import { X, ZoomIn, ZoomOut, RotateCw, Check } from 'lucide-react';

const AVATAR_SIZE = 200; // 输出尺寸 200x200

export default function AvatarCropperModal({ imageUrl, onClose, onSave }) {
  const cropperRef = useRef(null);

  // 缩放
  const handleZoom = useCallback((delta) => {
    const cropper = cropperRef.current?.cropper;
    if (cropper) {
      cropper.zoom(delta);
    }
  }, []);

  // 旋转
  const handleRotate = useCallback(() => {
    const cropper = cropperRef.current?.cropper;
    if (cropper) {
      cropper.rotate(90);
    }
  }, []);

  // 保存
  const handleSave = useCallback(() => {
    const cropper = cropperRef.current?.cropper;
    if (!cropper) return;

    // 获取裁剪后的 canvas
    const croppedCanvas = cropper.getCroppedCanvas({
      width: AVATAR_SIZE,
      height: AVATAR_SIZE,
      imageSmoothingEnabled: true,
      imageSmoothingQuality: 'high',
    });

    if (!croppedCanvas) return;

    // 转换为 Blob（JPEG 格式，质量 0.9）
    croppedCanvas.toBlob(
      (blob) => {
        if (blob) {
          onSave(blob);
        }
      },
      'image/jpeg',
      0.9
    );
  }, [onSave]);

  const modal = (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      background: 'rgba(0,0,0,0.85)', zIndex: 999999,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      backdropFilter: 'blur(5px)'
    }}>
      <div style={{
        width: 500, maxWidth: '95vw',
        background: '#1a1a1d', borderRadius: 16,
        overflow: 'hidden', border: '1px solid var(--border-subtle)',
        boxShadow: '0 40px 80px rgba(0,0,0,0.8)'
      }}>
        {/* Header */}
        <div style={{
          height: 56, padding: '0 20px',
          background: '#0f0f12',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          borderBottom: '1px solid var(--border-subtle)'
        }}>
          <h3 style={{ margin: 0, fontSize: 16, color: '#fff', fontWeight: 600 }}>
            裁剪头像
          </h3>
          <button
            onClick={onClose}
            style={{
              width: 32, height: 32, borderRadius: 6,
              background: 'rgba(255,255,255,0.05)',
              border: '1px solid var(--border-subtle)',
              color: '#94a3b8', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center'
            }}
          >
            <X size={18} />
          </button>
        </div>

        {/* Cropper */}
        <div style={{ height: 350, background: '#000' }}>
          <Cropper
            ref={cropperRef}
            src={imageUrl}
            style={{ height: '100%', width: '100%' }}
            aspectRatio={1} // 1:1 方形
            viewMode={1}
            guides={true}
            center={true}
            background={false}
            responsive={true}
            autoCropArea={0.8}
            checkOrientation={false}
          />
        </div>

        {/* Footer */}
        <div style={{
          height: 64, padding: '0 20px',
          background: '#0f0f12',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          borderTop: '1px solid var(--border-subtle)'
        }}>
          {/* 工具按钮 */}
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              onClick={() => handleZoom(-0.1)}
              style={{
                width: 36, height: 36, borderRadius: 6,
                background: 'rgba(255,255,255,0.05)',
                border: '1px solid var(--border-subtle)',
                color: '#94a3b8', cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center'
              }}
              title="缩小"
            >
              <ZoomOut size={16} />
            </button>
            <button
              onClick={() => handleZoom(0.1)}
              style={{
                width: 36, height: 36, borderRadius: 6,
                background: 'rgba(255,255,255,0.05)',
                border: '1px solid var(--border-subtle)',
                color: '#94a3b8', cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center'
              }}
              title="放大"
            >
              <ZoomIn size={16} />
            </button>
            <button
              onClick={handleRotate}
              style={{
                width: 36, height: 36, borderRadius: 6,
                background: 'rgba(255,255,255,0.05)',
                border: '1px solid var(--border-subtle)',
                color: '#94a3b8', cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center'
              }}
              title="旋转 90°"
            >
              <RotateCw size={16} />
            </button>
          </div>

          {/* 操作按钮 */}
          <div style={{ display: 'flex', gap: 12 }}>
            <button
              onClick={onClose}
              style={{
                padding: '0 20px', height: 36, borderRadius: 6,
                background: 'rgba(255,255,255,0.05)',
                border: '1px solid var(--border-subtle)',
                color: '#94a3b8', cursor: 'pointer', fontSize: 14
              }}
            >
              取消
            </button>
            <button
              onClick={handleSave}
              style={{
                padding: '0 20px', height: 36, borderRadius: 6,
                background: '#3b82f6', border: 'none',
                color: '#fff', cursor: 'pointer', fontSize: 14, fontWeight: 500,
                display: 'flex', alignItems: 'center', gap: 6
              }}
            >
              <Check size={16} /> 确定
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
}
